import React, { useEffect } from 'react'
import { Button } from '@/components/ui/button'

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'danger', // 'danger' | 'primary' | 'success'
  onConfirm,
  onClose,
  children,
}) {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null

  const confirmButtonVariant =
    confirmVariant === 'danger'
      ? 'destructive'
      : confirmVariant === 'primary'
        ? 'brand'
        : 'success'

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Confirmation dialog'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="p-5">
          <div className="flex items-start gap-3">
            {confirmVariant === 'danger' ? (
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-700">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-5a1 1 0 112 0 1 1 0 01-2 0zm0-8a1 1 0 012 0v5a1 1 0 11-2 0V5z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            ) : confirmVariant === 'primary' ? (
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-700">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            ) : (
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-green-50 text-green-700">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.172 7.707 8.879a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}

            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              {message ? <p className="mt-1 text-sm text-gray-600">{message}</p> : null}
            </div>
          </div>

          {children ? (
            <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-700">{children}</div>
          ) : null}

          <div className="mt-5 flex justify-end gap-2">
            <Button type="button" variant="muted" onClick={onClose}>
              {cancelText}
            </Button>
            <Button type="button" variant={confirmButtonVariant} onClick={onConfirm}>
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
